import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Header } from "components/DesignSystem/Header/Header";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Filter } from "components/DesignSystem/Filter/Filter";
import Loader from "components/DesignSystem/Loader/loader";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import Slider from "components/DesignSystem/Slider/Slider";
import Table from "components/DesignSystem/Table/V2/Table";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { InvoiceSlider } from "components/InvoiceList/InvoiceSlider";
import {
  InvoiceAmountColumn,
  InvoiceDateColumn,
  InvoiceNumberColumn,
  InvoiceTitleColumn,
  SentToColumn,
} from "components/InvoiceColumns/InvoiceColumns";
import { YYYY_MM_DD } from "constants/date";
import { ACCOUNTING_METHODS } from "constants/bookkeeping";
import dayjs from "dayjs";
import { Field, FieldProps, Form, Formik } from "formik";
import { parse, stringify } from "qs";
import { motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  useExportAPAgingSummaryMutation,
  useExportARAgingSummaryMutation,
  useGetAgingSummaryQuery,
  useGetAPAgingSummaryQuery,
} from "store/apis/reports";
import { useGetAllInvoicesQuery } from "store/apis/invoices";
import { ARAgingSummary } from "types/Models/report";
import { BackendError } from "types/utils/error";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { openLink } from "utils/openLink";
import { pluralize } from "utils/pluralize";
import * as Yup from "yup";
import { useGetAllBillsQuery } from "store/apis/vendorBills";
import { billColumns } from "components/VendorBills/BillList";
import { BillSlider } from "components/VendorBills/BillSlider";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { AddToClosing } from "./AddToClosing";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { ArrowDown } from "components/icons/ArrowDown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { USD } from "constants/currency";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";

export type FilterValues = {
  DATE: string;
  ACCOUNTING_METHOD: keyof typeof ACCOUNTING_METHODS;
  DAYS_PER_AGING: number;
  NUMBER_OF_PERIODS: number;
};
type FilterName = keyof FilterValues;

const AGING_FILTER_COUNT = 4;

const AgingSummaryFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: (name: FilterName, newValue: FilterValues[FilterName]) => void;
}) => {
  const { isMobile } = useWindowSize();
  const appliedFilterCount = AGING_FILTER_COUNT;

  return (
    <Formik
      initialValues={{
        DATE: filterValues.DATE,
        DAYS_PER_AGING: filterValues.DAYS_PER_AGING,
        NUMBER_OF_PERIODS: filterValues.NUMBER_OF_PERIODS,
        ACCOUNTING_METHOD: filterValues.ACCOUNTING_METHOD,
      }}
      onSubmit={() => {}}
      validationSchema={Yup.object().shape({
        DATE: Yup.date().required("Required"),
        DAYS_PER_AGING: Yup.number()
          .min(1, "Can be between 1-360 only")
          .max(360, "Can be between 1-360 only")
          .required("Required")
          .typeError("Must be a number"),
        NUMBER_OF_PERIODS: Yup.number()
          .min(1, "Can be between 1-12 only")
          .max(12, "Can be between 1-12 only")
          .required("Required")
          .typeError("Must be a number"),
      })}
      validateOnBlur
    >
      {({ values, errors, setFieldValue }) => {
        return (
          <Filter.Root
            defaultValue="DATE"
            hideDivider={isMobile}
            title={
              isMobile ? (
                <span className="t-text-text-30">({appliedFilterCount})</span>
              ) : (
                "Filters"
              )
            }
            capsule={
              <span className="t-contents sm:t-hidden">
                <Chip
                  filterType="DATE"
                  onRemove={() => {}}
                  isActive
                  isRemovable={false}
                >
                  As of {dayjs(filterValues.DATE).format("DD MMM YYYY")}
                </Chip>

                <Chip
                  filterType="ACCOUNTING_METHOD"
                  onRemove={() => {}}
                  isActive
                  isRemovable={false}
                >
                  <span className="first-letter:t-capitalize t-lowercase">
                    {filterValues.ACCOUNTING_METHOD} method
                  </span>
                </Chip>

                <Chip
                  filterType="DAYS_PER_AGING"
                  onRemove={() => {}}
                  isActive
                  isRemovable={false}
                >
                  {filterValues.DAYS_PER_AGING} days/period
                </Chip>

                <Chip
                  filterType="NUMBER_OF_PERIODS"
                  onRemove={() => {}}
                  isActive
                  isRemovable={false}
                >
                  {filterValues.NUMBER_OF_PERIODS} period
                </Chip>
              </span>
            }
          >
            <Form>
              <Filter.Portal>
                <Filter.List>
                  <Filter.ListItem value="DATE">Date</Filter.ListItem>
                  <Filter.ListItem value="ACCOUNTING_METHOD">
                    Accounting method
                  </Filter.ListItem>
                  <Filter.ListItem value="DAYS_PER_AGING">
                    Days per aging period
                  </Filter.ListItem>
                  <Filter.ListItem value="NUMBER_OF_PERIODS">
                    Number of periods
                  </Filter.ListItem>
                </Filter.List>
                <Filter.Body value="DATE" block>
                  <Field name="DATE">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        maxDate={new Date()}
                        label="As of date"
                        portalId="DATE"
                        onDateChange={(date) => {
                          const formatted = dayjs(date).format(YYYY_MM_DD);
                          setFieldValue("DATE", formatted);
                          updateFilter("DATE", formatted);
                        }}
                      />
                    )}
                  </Field>
                </Filter.Body>
                <Filter.Body value="ACCOUNTING_METHOD" block>
                  <RadioGroup.Root
                    className="t-mb-6"
                    value={values.ACCOUNTING_METHOD}
                    onValueChange={(value) => {
                      setFieldValue("ACCOUNTING_METHOD", value);
                      updateFilter(
                        "ACCOUNTING_METHOD",
                        value as FilterValues["ACCOUNTING_METHOD"]
                      );
                    }}
                  >
                    <RadioGroup.Content>
                      <RadioGroup.Item value={ACCOUNTING_METHODS.CASH}>
                        Cash
                      </RadioGroup.Item>
                      <RadioGroup.Item value={ACCOUNTING_METHODS.ACCRUAL}>
                        Accrual
                      </RadioGroup.Item>
                    </RadioGroup.Content>
                  </RadioGroup.Root>
                </Filter.Body>
                <Filter.Body value="DAYS_PER_AGING" block>
                  <TextInput
                    label="Days per aging period"
                    max={360}
                    min={1}
                    name="DAYS_PER_AGING"
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (!errors.DAYS_PER_AGING && val >= 1 && val <= 360) {
                        updateFilter("DAYS_PER_AGING", val);
                      }
                    }}
                  />
                </Filter.Body>
                <Filter.Body value="NUMBER_OF_PERIODS" block>
                  <TextInput
                    label="Number of periods"
                    max={12}
                    min={1}
                    name="NUMBER_OF_PERIODS"
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (!errors.NUMBER_OF_PERIODS && val >= 1 && val <= 12) {
                        updateFilter("NUMBER_OF_PERIODS", val);
                      }
                    }}
                  />
                </Filter.Body>
              </Filter.Portal>
            </Form>
          </Filter.Root>
        );
      }}
    </Formik>
  );
};

export type AgingData = {
  customerId: string;
  ids?: string[];
};

export const InvoiceTable = ({ ids }: { ids: string[] }) => {
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const groupId = group?.uuid;
  const [invoiceId, setInvoiceId] = useState<string>("");
  const invoiceSlider = useModal();
  const { search } = useLocation();
  const [displayCurrency, setDisplayCurrency] = useState<
    "INVOICE_CURRENCY" | "USD"
  >("USD");

  const { data, isLoading } = useGetAllInvoicesQuery(
    {
      groupId,
      entityId,
      searchTerm: "",
      invoiceIds: ids.join(","),
    },
    { skip: !groupId || !entityId }
  );

  const { invoices = [] } = data || {};

  const invoiceColumns = [
    InvoiceDateColumn,
    InvoiceNumberColumn,
    InvoiceTitleColumn,
    SentToColumn,
    InvoiceAmountColumn,
  ];

  const table = useReactTable({
    data: invoices,
    columns: invoiceColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    meta: {
      displayCurrency,
      setDisplayCurrency,
    },
  });

  return (
    <tr
      className="t-w-full t-z-filter-head t-overscroll-x-none"
      style={{ height: `${50 * (invoices?.length + 1) + 30}px ` }}
    >
      {isLoading ? (
        <td className="t-w-full" colSpan={100}>
          <div className="t-w-full t-flex t-justify-center t-py-1">
            <Loader size="small" />
          </div>
        </td>
      ) : (
        <td className="t-absolute">
          <div className="t-py-0.5 t-w-full">
            {invoices.length === 0 ? (
              <div className="t-w-full t-flex t-justify-center t-text-body-sm">
                No invoices found
              </div>
            ) : (
              <div className="t-shadow-light-30 t-rounded t-flex-col t-gap-2">
                <div className="t-flex t-justify-end t-text-body-sm t-text-text-60 t-py-2 t-pr-2.5">
                  {pluralize(invoices.length, "invoice", "invoices")}
                </div>
                <TableUI
                  layout="fixed"
                  table={table}
                  onRowClick={(row) => {
                    setInvoiceId(row.original.uuid);
                    invoiceSlider.open();
                  }}
                />
                <Slider.Root
                  open={invoiceSlider.isOpen}
                  onOpenChange={invoiceSlider.close}
                >
                  <Slider.Content open={invoiceSlider.isOpen}>
                    <InvoiceSlider
                      invoiceId={invoiceId}
                      redirectLink={`books/invoices-and-customers/${invoiceId}${search}`}
                    />
                  </Slider.Content>
                </Slider.Root>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

const summaryBillColumns = [
  billColumns.bill_date(20),
  billColumns.bill_number(20),
  billColumns.title(20),
  billColumns.due_date(20, "Due Date"),
  billColumns.due_balance(20),
];

export const BillTable = ({ ids }: { ids: string[] }) => {
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const groupId = group?.uuid;
  const [childHeight, setChildHeight] = useState(0);
  const observer = useRef<ResizeObserver>(null);
  const element = useRef<HTMLElement>(null);

  const [displayCurrency, setDisplayCurrency] = useState<
    "BILL_CURRENCY" | typeof USD
  >("BILL_CURRENCY");

  const slider = useModal();
  const [billId, setBillId] = useState<string | undefined>();

  const openSlider = ({ billId }: { billId: string }) => {
    setBillId(billId);
    slider.open();
  };
  const closeSlider = () => {
    setBillId(undefined);
    slider.close();
  };

  const handleRef = (node: HTMLElement | null) => {
    if (!node) {
      return;
    }

    const handleResize = () => {
      setChildHeight(node.clientHeight || 0);
    };

    handleResize();
    const resizeObserver = new ResizeObserver((a) => a);
    resizeObserver?.observe?.(node);

    // @ts-ignore
    element.current = node;
    // @ts-ignore
    observer.current = resizeObserver;
  };

  useEffect(() => {
    observer.current?.unobserve(element.current!);
  });

  const { data, isLoading } = useGetAllBillsQuery(
    {
      bill_type: "BILL",
      entityId,
      groupId,
      search_term: "",
      ids: ids.join(","),
      minAmount: "",
      maxAmount: "",
      startDate: "",
      endDate: "",
      dueDateStartDate: "",
      dueDateEndDate: "",
      vendorIds: "",
      statuses: [],
      sources: [],
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const { bills = [] } = data || {};

  const table = useReactTable({
    data: bills,
    columns: summaryBillColumns,
    defaultColumn: {
      minSize: 5,
    },
    getCoreRowModel: getCoreRowModel(),
    meta: {
      displayCurrency,
      setDisplayCurrency,
    },
  });

  return (
    <tr
      className="t-w-full t-z-filter-head t-overscroll-x-none"
      style={{ height: childHeight + 5 }}
    >
      {isLoading ? (
        <td className="t-w-full" colSpan={100}>
          <div className="t-w-full t-flex t-justify-center t-py-1">
            <Loader size="small" />
          </div>
        </td>
      ) : (
        <td className="t-absolute t-w-full">
          <div className="t-py-0.5 t-w-full">
            {bills.length === 0 ? (
              <div
                className="t-w-full t-flex t-justify-center t-text-body-sm t-py-2"
                ref={handleRef}
              >
                No invoices found
              </div>
            ) : (
              <div
                className="t-shadow-light-30 t-rounded t-flex-col t-gap-2"
                ref={handleRef}
              >
                <div className="t-flex t-justify-end t-text-body-sm t-text-text-60 t-py-2 t-pr-2.5">
                  {pluralize(bills.length, "invoice", "invoices")}
                </div>
                <TableUI
                  table={table}
                  onRowClick={(row) => {
                    openSlider({
                      billId: row.original.uuid,
                    });
                  }}
                />
                {billId && (
                  <BillSlider
                    close={closeSlider}
                    isOpen={slider.isOpen}
                    billId={billId}
                  />
                )}
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export const AgingSumarySaveExport = ({
  filterValues,
  type,
}: {
  filterValues: FilterValues;
  type: "AP" | "AR";
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { isCpa, isForeignCA } = useRoleBasedView();
  const addToClosingModal = useModal();
  const [getPreviewUrl, { isLoading: isPreviewLoading }] =
    useLazyGetPreviewUrlQuery();
  const [exportARAgingSummary, arAgingApistate] =
    useExportARAgingSummaryMutation();
  const [exportAPAgingSummary, apAgingApistate] =
    useExportAPAgingSummaryMutation();
  const [operation, setOperation] = useState<"EXPORT" | "ADD_TO_CLOSING">();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();

  const onExport = async (exportToMail: boolean = false, folderId?: string) => {
    try {
      setOperation("EXPORT");
      const payload = {
        entityId,
        date: filterValues.DATE,
        accountingMethod: filterValues.ACCOUNTING_METHOD,
        daysPerAgingPeriod: filterValues.DAYS_PER_AGING,
        numberOfAgingPeriods: filterValues.NUMBER_OF_PERIODS,
        export_to_mail: exportToMail,
        folderId,
      };

      if (type === "AR") {
        const document_id = await exportARAgingSummary(payload).unwrap();
        if (!exportToMail && !folderId) {
          const { download_url } = await getPreviewUrl({
            groupId: groupId!,
            fileId: document_id,
          }).unwrap();
          openLink(download_url);
        }
      }

      if (type === "AP") {
        const document_id = await exportAPAgingSummary(payload).unwrap();
        if (!exportToMail && !folderId) {
          const { download_url } = await getPreviewUrl({
            groupId: groupId!,
            fileId: document_id,
          }).unwrap();
          openLink(download_url);
        }
      }

      if (folderId) {
        setSavedDocumentId(document_id);
        savedToInkleDocsModal.open();
      } else {
        successToast({
          message: exportToMail ? "Report sent to email" : "Report downloaded",
        });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      setOperation(undefined);
    }
  };

  const onAddToMonthlyClosing = async () => {
    try {
      setOperation("ADD_TO_CLOSING");
      const payload = {
        entityId,
        date: filterValues.DATE,
        accountingMethod: filterValues.ACCOUNTING_METHOD,
        daysPerAgingPeriod: filterValues.DAYS_PER_AGING,
        numberOfAgingPeriods: filterValues.NUMBER_OF_PERIODS,
      };

      if (type === "AR") {
        await exportARAgingSummary(payload).unwrap();
      }
      if (type === "AP") {
        await exportAPAgingSummary(payload).unwrap();
      }
      addToClosingModal.open();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      setOperation(undefined);
    }
  };

  const isLoading = arAgingApistate.isLoading || apAgingApistate.isLoading;

  const document_id =
    type === "AR" ? arAgingApistate.data : apAgingApistate.data;

  return (
    <div className="t-justify-end t-flex t-gap-2 t-items-center">
      {(isCpa || isForeignCA) && (
        <Button
          size="xsmall"
          onClick={onAddToMonthlyClosing}
          isLoading={isLoading && operation === "ADD_TO_CLOSING"}
          disabled={isLoading && operation === "ADD_TO_CLOSING"}
        >
          Add to closing
        </Button>
      )}

      <Dropdown.Root modal={false}>
        <Dropdown.Trigger className="t-group" asChild>
          <div>
            <Button
              size="xsmall"
              customType="primary"
              disabled={isLoading || isPreviewLoading}
              isLoading={isLoading || isPreviewLoading}
            >
              Export
              <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                <ArrowDown color="currentColor" />
              </div>
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={4} align="end" side="bottom">
            <ExportOptions
              onLocalDownload={() => onExport(false)}
              onMailExport={() => onExport(true)}
              onSaveInInkleDocs={selectFolderModal.open}
            />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => onExport(false, folderId)}
        />
      )}

      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType={type === "AP" ? "AP Aging Summary" : "AR Aging Summary"}
        />
      )}

      {document_id && (
        <AddToClosing
          {...addToClosingModal}
          endDate={filterValues.DATE}
          key={filterValues.DATE ? filterValues.DATE : ""}
          reportFileId={document_id!}
          reportType={type === "AP" ? "AP_AGING" : "AR_AGING"}
        />
      )}
    </div>
  );
};

const useAgingSummary = ({
  entityId,
  filterValues,
  type,
}: {
  type: "AR" | "AP";
  entityId: string;
  filterValues: FilterValues;
}) => {
  const shouldSkipQuery =
    !entityId ||
    !filterValues.DATE ||
    !filterValues.ACCOUNTING_METHOD ||
    !filterValues.DAYS_PER_AGING ||
    !filterValues.NUMBER_OF_PERIODS;

  const queryParams = {
    entityId,
    date: filterValues.DATE,
    ACCOUNTING_METHODS: filterValues.ACCOUNTING_METHOD,
    daysPerAgingPeriod: filterValues.DAYS_PER_AGING,
    numberOfAgingPeriods: filterValues.NUMBER_OF_PERIODS,
  };

  const { data: apAgingResponse, ...apAgingQueryState } =
    useGetAPAgingSummaryQuery(queryParams, {
      skip: shouldSkipQuery || type === "AR",
      refetchOnMountOrArgChange: true,
    });

  const { data: arAgingResponse, ...arAgingQueryState } =
    useGetAgingSummaryQuery(queryParams, {
      skip: shouldSkipQuery || type === "AP",
      refetchOnMountOrArgChange: true,
    });

  const data =
    type === "AP"
      ? apAgingResponse?.ap_aging_summary
      : arAgingResponse?.ar_aging_summary;

  const agingQueryState = type === "AP" ? apAgingQueryState : arAgingQueryState;

  return { data, ...agingQueryState };
};

type AgingRenderTable = (args: {
  customerId?: string;
  ids?: string[];
}) => React.ReactNode;

type AgingSummaryProps =
  | {
      type: "AR";
      renderTable: AgingRenderTable;
    }
  | {
      type: "AP";
      renderTable: AgingRenderTable;
    };

const AgingSummary = ({ renderTable, type }: AgingSummaryProps) => {
  const { isMobile } = useWindowSize();
  const query = useQuery();
  const date = query.get("DATE") || dayjs().format(YYYY_MM_DD).toString();
  const daysPerAging = query.get("DAYS_PER_AGING") || "30";
  const numberOfPeriods = query.get("NUMBER_OF_PERIODS") || "6";
  const othersUrl = useMemo(() => {
    const parsedSearch = parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const search = stringify(
      Object.fromEntries(
        Object.entries(parsedSearch).filter(([key]) =>
          ["entity", "company"].includes(key)
        )
      ),
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/live-financials/others${search}`;
  }, []);
  const title = type === "AR" ? "AR Aging" : "AP Aging";
  const entityId = useCurrentEntityId();

  const { data: settings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );

  const { values: filterValues, updateFilter } = useFilters<FilterValues>({
    initialValue: {
      DATE: date,
      ACCOUNTING_METHOD: settings?.accounting_method!,
      DAYS_PER_AGING: parseInt(daysPerAging),
      NUMBER_OF_PERIODS: parseInt(numberOfPeriods),
    },
    useQueryParams: true,
  });

  const [agingData, setAgingData] = useState<AgingData>({
    customerId: "",
    ids: [],
  });
  const [showInvoices, setShowInvoices] = useState(false);
  const [selectedRange, setSelectedRange] = useState("");

  const { customerId: selectedCustomerId } = agingData;

  const {
    data: agingSummary,
    isFetching,
    isSuccess,
  } = useAgingSummary({
    entityId,
    filterValues,
    type,
  });

  const uniqueRanges = agingSummary?.[0]?.aging_summary?.aging_buckets;

  const reportDatabyView = useMemo(() => {
    return agingSummary?.map((sum) => ({
      ...sum,
      id: sum.vendor_id ?? sum.customer_id ?? "",
      name: sum.vendor_name ?? sum.customer_name ?? "",
    }));
  }, [agingSummary]);

  const columnHelper = useMemo(
    () =>
      createColumnHelper<
        ARAgingSummary & {
          id: string;
          name: string;
        }
      >(),
    []
  );

  const onAmountClick = useCallback(
    ({
      range,
      customerId,
      ids,
    }: {
      range?: string;
      customerId: string;
      ids?: string[];
    }) => {
      if (showInvoices && customerId === selectedCustomerId) {
        setAgingData({
          customerId: "",
          ids: [],
        });
        setSelectedRange("");
        setShowInvoices(false);
      } else {
        setAgingData({
          customerId,
          ids,
        });
        setSelectedRange(range || "");
        setShowInvoices(true);
      }
    },
    [showInvoices, selectedCustomerId]
  );

  const dynamicViewColumns = useMemo(
    () =>
      uniqueRanges?.map(({ range }) =>
        columnHelper.accessor(
          (row) =>
            row?.aging_summary?.aging_buckets?.find(
              (bucket) => bucket.range === range
            )?.amount || 0,
          {
            id: range,
            header: () => (
              <div className="t-flex t-justify-end t-ml-4">{range}</div>
            ),
            footer: () => {
              const rangeTotal =
                reportDatabyView?.reduce(
                  (sum, row) =>
                    sum +
                    (row.aging_summary?.aging_buckets?.find(
                      (bucket) => bucket.range === range
                    )?.amount || 0),
                  0
                ) || 0;

              return (
                <div className="t-flex t-justify-end t-ml-4">
                  <AmountSuperScript amount={rangeTotal} />
                </div>
              );
            },
            cell: ({ getValue, row }) => {
              const isSelected =
                selectedCustomerId === row.original.id &&
                range === selectedRange;

              const ids =
                row?.original.aging_summary?.aging_buckets?.find(
                  (bucket) => bucket.range === range
                )?.ids || [];

              return (
                <div className="t-flex t-justify-end t-ml-4">
                  {getValue() ? (
                    <button
                      className={classNames(
                        "all:unset t-text-body-sm t-ml-auto hover:!t-underline hover:!t-text-purple",
                        {
                          "t-underline t-text-purple": isSelected,
                        }
                      )}
                      onClick={() =>
                        onAmountClick({
                          range,
                          customerId: row.original.id,
                          ids: ids,
                        })
                      }
                    >
                      <AmountSuperScript amount={getValue()} />
                    </button>
                  ) : (
                    "-"
                  )}
                </div>
              );
            },
            size: 114,
          }
        )
      ) || [],
    [
      uniqueRanges,
      selectedCustomerId,
      selectedRange,
      onAmountClick,
      columnHelper,
      reportDatabyView,
    ]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: type === "AP" ? "Vendor name" : "Customer name",
        size: 300,
        footer: () => "Total",
        cell: ({ row, getValue }) => {
          const name = getValue();

          return (
            <div
              style={{
                paddingLeft: `${row.depth * 1}rem`,
              }}
              className={classNames("t-flex t-items-center")}
            >
              <div className="t-flex t-justify-end t-h-full t-items-center t-gap-3">
                {name}
              </div>
            </div>
          );
        },
      }),
      ...dynamicViewColumns,
      columnHelper.accessor("aging_summary.total", {
        header: "Total",
        id: "total",
        footer: () => {
          const grandTotal =
            reportDatabyView?.reduce(
              (sum, row) => sum + (row.aging_summary?.total || 0),
              0
            ) || 0;

          return <AmountSuperScript amount={grandTotal} />;
        },
        cell: ({ getValue }) => {
          return (
            <span className="all:unset t-text-body-sm t-ml-auto">
              <AmountSuperScript amount={getValue()} />
            </span>
          );
        },
        minSize: 60,
        size: 60,
      }),
    ],
    [columnHelper, dynamicViewColumns, type, reportDatabyView]
  );

  const table = useReactTable({
    data: reportDatabyView || [],
    columns: columns,
    state: {
      columnPinning: isMobile ? {} : { left: ["name"], right: ["total"] },
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DashboardContainer className="t-h-full t-w-full t-flex t-px-5 sm:t-px-3 sm:t-h-auto sm:t-overflow-x-visible">
      <DashboardContainer.Header className="t-sticky t-top-0 t-bg-white sm:t-static">
        <Header
          v2
          title={<span className="t-font-medium t-text-text-60">{title}</span>}
          breadcrumbs={[{ name: "Report List", link: othersUrl }]}
        />
        <div className="t-flex t-flex-col t-gap-4">
          <div className="t-flex t-justify-between sm:t-flex-wrap sm:t-gap-2">
            <AgingSummaryFilter
              filterValues={filterValues}
              updateFilter={updateFilter}
            />
            <AgingSumarySaveExport type={type} filterValues={filterValues} />
          </div>
          <AccountingMethodBanner
            selectedAccountingMethod={filterValues.ACCOUNTING_METHOD}
            entityId={entityId}
          />
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content className="sm:t-overflow-visible">
        <Async.Root {...{ isEmpty: false, isLoading: isFetching, isSuccess }}>
          <Async.Empty>
            <div className="t-text-body-sm t-flex t-justify-center t-h-full t-w-full">
              No invoices due
            </div>
          </Async.Empty>
          <Async.Success>
            <div className="t-pb-20 t-relative t-pt-6">
              <Table.Container>
                <Table.Content className="t-h-full">
                  <Table.Head>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Table.HeadRow key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => {
                          const styles = getCommonPinningStyles(header.column);
                          const isPinnedColumn = header.column.getIsPinned();
                          return (
                            <Table.HeadCell
                              key={header.id}
                              style={{
                                ...styles,
                                minWidth: header.column.getSize(),
                              }}
                              className={classNames({
                                "!t-bg-surface-lighter-grey": isPinnedColumn,
                              })}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </Table.HeadCell>
                          );
                        })}
                      </Table.HeadRow>
                    ))}
                  </Table.Head>
                  <Table.Body>
                    {table.getRowModel().rows.map((row) => {
                      const isInvoiceVisible =
                        showInvoices &&
                        agingData.customerId === row.original.id;
                      return (
                        <>
                          <motion.tr
                            key={row.id}
                            aria-label={row.original.name}
                            aria-disabled={!row.getCanExpand()}
                            aria-expanded={row.getIsExpanded()}
                            layout
                            className={classNames(
                              "t-select-none t-px-3 t-text-body-sm t-group t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0"
                            )}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const styles = getCommonPinningStyles(
                                cell.column
                              );

                              return (
                                <Table.Cell
                                  key={cell.id}
                                  style={{
                                    ...styles,
                                    minWidth: cell.column.getSize(),
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </Table.Cell>
                              );
                            })}
                          </motion.tr>
                          {isInvoiceVisible &&
                            renderTable({
                              customerId: agingData.customerId,
                              ids: agingData.ids,
                            })}
                        </>
                      );
                    })}
                  </Table.Body>
                  <tfoot className="t-bg-surface-lighter-grey t-text-text-100 t-font-medium t-border-0 t-border-solid t-border-neutral-0 t-border-t">
                    {table.getFooterGroups().map((footerGroup) => (
                      <Table.Row key={footerGroup.id}>
                        {footerGroup.headers.map((header) => {
                          const styles = getCommonPinningStyles(header.column);
                          const isPinnedColumn = header.column.getIsPinned();
                          return (
                            <Table.Cell
                              key={header.id}
                              style={{
                                ...styles,
                                minWidth: header.column.getSize(),
                              }}
                              className={classNames({
                                "!t-bg-surface-lighter-grey": isPinnedColumn,
                              })}
                            >
                              {flexRender(
                                header.column.columnDef.footer,
                                header.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </Table.Row>
                    ))}
                  </tfoot>
                </Table.Content>
              </Table.Container>
            </div>
          </Async.Success>
        </Async.Root>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};

export const ARAging = () => {
  return (
    <AgingSummary
      type="AR"
      renderTable={(args) =>
        Boolean(args.ids?.length) && (
          <div className="t-mb-10">
            <InvoiceTable ids={args.ids ?? []} />
          </div>
        )
      }
    />
  );
};

export const APAging = () => {
  return (
    <AgingSummary
      type="AP"
      renderTable={(args) =>
        Boolean(args.ids?.length) && (
          <div className="t-mb-10">
            <BillTable ids={args.ids ?? []} />
          </div>
        )
      }
    />
  );
};
